/*
 * Cedarfur Cart Drawer — open/close mechanics
 * ------------------------------------------------------------------
 * Scope: drawer UI state only (open, close, focus, scroll-lock, Esc,
 * overlay click). Cart fetching, item rendering, and qty-change sync
 * live in assets/cedarfur-cart.js.liquid (sibling agent).
 *
 * Public API:
 *   window.cfCartDrawer = {
 *     open():  open the drawer (sibling JS calls after add-to-cart)
 *     close(): close the drawer
 *     isOpen(): boolean
 *   }
 *
 * Selector contract (mirrors snippets/cart-drawer.liquid):
 *   #cf-cart-drawer                drawer root
 *   .cf-cart-drawer--open          open-state class on root
 *   [data-cf-drawer-open]          open trigger (header cart icon)
 *   [data-cf-drawer-close]         close button
 *   [data-cf-drawer-overlay]       backdrop (click closes)
 */
(function () {
  'use strict';

  var DRAWER_ID = 'cf-cart-drawer';
  var OPEN_CLASS = 'cf-cart-drawer--open';
  var BODY_LOCK_CLASS = 'cf-cart-drawer-open';

  var FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  var drawer = null;
  var panel = null;
  var closeBtn = null;
  var overlay = null;
  var lastFocusedTrigger = null;
  var isOpen = false;

  function getFocusable() {
    if (!panel) return [];
    return Array.prototype.slice
      .call(panel.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter(function (el) {
        // Skip hidden / display:none
        if (el.hasAttribute('hidden')) return false;
        if (el.offsetParent === null && el !== document.activeElement) return false;
        return true;
      });
  }

  function trapTab(event) {
    if (event.key !== 'Tab') return;
    var focusable = getFocusable();
    if (focusable.length === 0) {
      event.preventDefault();
      if (panel) panel.focus();
      return;
    }
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    var active = document.activeElement;

    if (event.shiftKey) {
      if (active === first || !panel.contains(active)) {
        event.preventDefault();
        last.focus();
      }
    } else {
      if (active === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  function onKeyDown(event) {
    if (!isOpen) return;
    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key === 'Tab') {
      trapTab(event);
    }
  }

  function open(triggerEl) {
    if (!drawer) drawer = document.getElementById(DRAWER_ID);
    if (!drawer) return;

    if (!panel) panel = drawer.querySelector('.cf-cart-drawer__panel');
    if (!closeBtn) closeBtn = drawer.querySelector('[data-cf-drawer-close]');
    if (!overlay) overlay = drawer.querySelector('[data-cf-drawer-overlay]');

    if (isOpen) return;

    // Remember what to restore focus to on close.
    lastFocusedTrigger =
      triggerEl ||
      (document.activeElement && document.activeElement !== document.body
        ? document.activeElement
        : document.querySelector('[data-cf-drawer-open]'));

    drawer.hidden = false;
    // Force layout so the transition runs from translateX(100%).
    // eslint-disable-next-line no-unused-expressions
    drawer.offsetHeight;

    drawer.classList.add(OPEN_CLASS);
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add(BODY_LOCK_CLASS);
    isOpen = true;

    // Move focus to the close button (or panel if missing).
    requestAnimationFrame(function () {
      if (closeBtn) {
        closeBtn.focus();
      } else if (panel) {
        panel.focus();
      }
    });

    document.addEventListener('keydown', onKeyDown);
  }

  function close() {
    if (!drawer || !isOpen) return;

    drawer.classList.remove(OPEN_CLASS);
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove(BODY_LOCK_CLASS);
    isOpen = false;

    document.removeEventListener('keydown', onKeyDown);

    // Wait for transition before hiding (so the slide-out is visible).
    var prefersReducedMotion =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var hideDelay = prefersReducedMotion ? 0 : 280;

    window.setTimeout(function () {
      if (!isOpen && drawer) drawer.hidden = true;
    }, hideDelay);

    // Restore focus to the element that opened the drawer.
    if (lastFocusedTrigger && typeof lastFocusedTrigger.focus === 'function') {
      try {
        lastFocusedTrigger.focus();
      } catch (e) {
        /* no-op */
      }
    }
  }

  function onDocumentClick(event) {
    var openTrigger = event.target.closest && event.target.closest('[data-cf-drawer-open]');
    if (openTrigger) {
      event.preventDefault();
      open(openTrigger);
      return;
    }

    var closeTrigger = event.target.closest && event.target.closest('[data-cf-drawer-close]');
    if (closeTrigger && drawer && drawer.contains(closeTrigger)) {
      event.preventDefault();
      close();
      return;
    }

    var overlayTrigger = event.target.closest && event.target.closest('[data-cf-drawer-overlay]');
    if (overlayTrigger && drawer && drawer.contains(overlayTrigger)) {
      close();
    }
  }

  function init() {
    drawer = document.getElementById(DRAWER_ID);
    if (!drawer) return;
    panel = drawer.querySelector('.cf-cart-drawer__panel');
    closeBtn = drawer.querySelector('[data-cf-drawer-close]');
    overlay = drawer.querySelector('[data-cf-drawer-overlay]');

    // Delegated click handler — covers triggers added later by other scripts.
    document.addEventListener('click', onDocumentClick);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Public API for sibling cart JS (e.g. open after successful add-to-cart).
  window.cfCartDrawer = {
    open: function () {
      open();
    },
    close: function () {
      close();
    },
    isOpen: function () {
      return isOpen;
    }
  };
})();
